import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const accessOptions = [
  { value: "cross", label: "CROSS" },
  { value: "funcional", label: "FUNCIONAL" },
  { value: "maquinas", label: "MAQUINAS" },
];

export default function MembershipFormModal({
  open,
  onClose,
  onSubmit,
  plan,
  loading,
}) {
  const [form, setForm] = useState({
    name: "",
    price: "",
    duration_value: "",
    duration_unit: "days",
    usage_type: "calendar",
    available_accesses: [],
    is_active: true,
  });

  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) return;

    setForm({
      name: plan?.name || "",
      price: plan?.price || "",
      duration_value: plan?.duration_value || "",
      duration_unit: plan?.duration_unit || "days",
      usage_type: plan?.usage_type || "calendar",
      available_accesses: Array.isArray(plan?.available_accesses) ? plan.available_accesses : [],
      is_active: typeof plan?.is_active === "boolean" ? plan.is_active : true,
    });

    setLocalError("");
  }, [open, plan]);

  const handleChange = (field) => (event) => {
    const value =
      event?.target?.type === "checkbox" ? event.target.checked : event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleAccess = (access) => {
    setForm((prev) => {
      const exists = prev.available_accesses.includes(access);

      return {
        ...prev,
        available_accesses: exists
          ? prev.available_accesses.filter((item) => item !== access)
          : [...prev.available_accesses, access],
      };
    });
  };

  const handleSubmit = async () => {
    setLocalError("");

    if (!form.name.trim()) {
      setLocalError("El nombre es obligatorio");
      return;
    }

    if (!form.price || Number(form.price) < 0) {
      setLocalError("El precio debe ser válido");
      return;
    }

    if (!form.duration_value || Number(form.duration_value) <= 0) {
      setLocalError("La duración debe ser mayor a cero");
      return;
    }

    if (!form.available_accesses.length) {
      setLocalError("Debes seleccionar al menos un acceso");
      return;
    }

    const payload = {
      name: form.name.trim(),
      price: form.price,
      duration_value: Number(form.duration_value),
      duration_unit: form.duration_unit,
      usage_type: form.usage_type,
      available_accesses: form.available_accesses,
      is_active: form.is_active,
    };

    const result = await onSubmit(payload);

    if (!result?.ok && result?.error) {
      setLocalError(result.error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "18px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: "#0f172a",
          pb: 1,
        }}
      >
        {plan ? "Editar membresía" : "Nueva membresía"}
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        <Stack spacing={1.5}>
          <TextField
            label="Nombre"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            size="small"
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="Precio"
              value={form.price}
              onChange={handleChange("price")}
              fullWidth
              size="small"
            />

            <TextField
              label="Duración"
              type="number"
              value={form.duration_value}
              onChange={handleChange("duration_value")}
              fullWidth
              size="small"
              inputProps={{
                min: 1,
              }}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              select
              label="Unidad"
              value={form.duration_unit}
              onChange={handleChange("duration_unit")}
              fullWidth
              size="small"
            >
              <MenuItem value="day">Día</MenuItem>
              <MenuItem value="days">Días</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo de uso"
              value={form.usage_type}
              onChange={handleChange("usage_type")}
              fullWidth
              size="small"
            >
              <MenuItem value="calendar">Por tiempo</MenuItem>
              <MenuItem value="visits">Por visitas</MenuItem>
            </TextField>
          </Stack>

          <Box
            sx={{
              border: "1px solid rgba(148,163,184,0.18)",
              borderRadius: "14px",
              p: 1.5,
              bgcolor: "#f8fafc",
            }}
          >
            <Typography
              sx={{
                fontSize: 13.2,
                fontWeight: 700,
                color: "#0f172a",
                mb: 1.2,
              }}
            >
              Accesos disponibles
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {accessOptions.map((item) => {
                const selected = form.available_accesses.includes(item.value);

                return (
                  <Chip
                    key={item.value}
                    label={item.label}
                    clickable
                    onClick={() => handleToggleAccess(item.value)}
                    sx={{
                      height: 30,
                      borderRadius: "9px",
                      fontSize: 12.4,
                      fontWeight: 600,
                      bgcolor: selected ? "rgba(59,130,246,0.10)" : "#ffffff",
                      color: selected ? "#1d4ed8" : "#475569",
                      border: selected
                        ? "1px solid rgba(59,130,246,0.24)"
                        : "1px solid rgba(148,163,184,0.22)",
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_active}
                onChange={handleChange("is_active")}
              />
            }
            label="Membresía activa"
            sx={{
              ml: -0.8,
              "& .MuiFormControlLabel-label": {
                fontSize: 13.5,
                fontWeight: 500,
                color: "#334155",
              },
            }}
          />

          {localError && (
            <Typography
              sx={{
                fontSize: 13,
                color: "#dc2626",
                fontWeight: 500,
              }}
            >
              {localError}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.2, pt: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            height: 36,
            px: 1.4,
            borderRadius: "9px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.8,
            color: "#334155",
            bgcolor: "#ffffff",
            borderColor: "rgba(51,65,85,0.18)",
            "&:hover": {
              borderColor: "rgba(51,65,85,0.28)",
              bgcolor: "#f8fafc",
            },
          }}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            height: 36,
            px: 1.4,
            borderRadius: "9px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.8,
            bgcolor: "#0f172a",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#111827",
              boxShadow: "none",
            },
          }}
        >
          {loading ? "Guardando..." : plan ? "Guardar cambios" : "Crear membresía"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}